import {
  ArrowLeftOutline,
  CheckPhoneNumberUpdateResponse,
  getError,
  useToast,
  ExclamationHexagonBold as ExclamationHexagon,
} from '@aslanpay/portal-common-lib';
import {
  Flex,
  Center,
  Spinner,
  HStack,
  PinInput,
  PinInputField,
  VStack,
  Button,
  Text,
  useBoolean,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import useChangePhoneNumber from 'app/hooks/useChangePhoneNumber';
import useCheckPhoneNumberUpdate from 'app/hooks/useCheckPhoneNumberUpdate';
import { useState } from 'react';

import { VerifyPhoneStepProps } from './types';

function VerifyPhoneStep({
  phoneNumber,
  transactionId,
  setTransactionId,
  setStep,
  onClose,
}: VerifyPhoneStepProps) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [changePhoneError, setChangePhoneError] = useState('');
  const [resendMessage, setResendMessage] = useState('Code not received?');
  const [isResendDisabled, setIsResendDisabled] = useBoolean();

  const { mutate: checkPhoneNumber, isLoading: isChekingPhone } = useCheckPhoneNumberUpdate({
    onSuccess: (data: CheckPhoneNumberUpdateResponse) => {
      setTransactionId(data.transactionId);
      setStep('VERIFY_PHONE');
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, it was not possible to check your phone number. Please try again.',
      });
    },
  });

  const handleResendCode = () => {
    checkPhoneNumber(phoneNumber);

    setIsResendDisabled.on();
    setResendMessage('A new verification code was sent!');
    setTimeout(() => {
      setResendMessage('Code not received?');
      setIsResendDisabled.off();
    }, 120000);
  };

  const { mutate: changePhoneNumber, isLoading: isChangingPhone } = useChangePhoneNumber({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchIndividual'] });

      toast({
        position: 'top-right',
        status: 'success',
        title: 'Two-factor authentication enabled',
        description: "You've successfully set up two-factor authentication.",
      });

      onClose();
    },
    onError: (error) => {
      const baseError = getError(error);

      if (baseError.status === 403) {
        setChangePhoneError('This code is incorrect. Please try again.');
      } else {
        setChangePhoneError('Something went wrong. Please try again');
      }
    },
  });

  const handleChangePhone = (code: string) => {
    changePhoneNumber({
      phoneNumber,
      transactionId,
      code,
    });
  };

  return (
    <Flex flexDir="column" align="flex-start" p="24px">
      <Button
        variant="link"
        leftIcon={<ArrowLeftOutline boxSize="24px" />}
        onClick={() => setStep('INSERT_PHONE')}
      >
        Back
      </Button>

      {changePhoneError && (
        <Flex
          w="full"
          p="16px"
          mt="25px"
          bg="alert.error"
          borderRadius="10px"
          alignItems="flex-start"
          gap="12px"
          boxShadow="xl"
        >
          <ExclamationHexagon boxSize="24px" color="system.error" />
          <Text textStyle="h6SemiBold" color="brand.error">
            {changePhoneError}
          </Text>
        </Flex>
      )}

      <Flex
        mt="20px"
        py="20px"
        borderRadius="10px"
        gap="8px"
        flexDirection="column"
        alignItems="center"
        bg="brand.lightPurple"
        w="full"
      >
        <Text textStyle="h6Medium" maxW="210px" color="brand.mutedPurple" textAlign="center">
          Please enter the code we&apos;ve sent to your phone
        </Text>

        {isChangingPhone ? (
          <Center width="full" height="100px">
            <Spinner color="brand.purple" emptyColor="brand.tint30" size="xl" />
          </Center>
        ) : (
          <HStack my="12px" spacing="15px" justify="center">
            <PinInput
              placeholder=""
              onComplete={handleChangePhone}
              size="lg"
              isInvalid={!!changePhoneError}
            >
              <PinInputField
                bg="brand.lightGray2"
                borderColor={changePhoneError ? 'system.error' : 'brand.mediumGray'}
                _focusVisible={{ borderWidth: '1px', borderColor: 'brand.purple' }}
              />
              <PinInputField
                bg="brand.lightGray2"
                borderColor={changePhoneError ? 'system.error' : 'brand.mediumGray'}
                _focusVisible={{ borderWidth: '1px', borderColor: 'brand.purple' }}
              />
              <PinInputField
                bg="brand.lightGray2"
                borderColor={changePhoneError ? 'system.error' : 'brand.mediumGray'}
                _focusVisible={{ borderWidth: '1px', borderColor: 'brand.purple' }}
              />
              <PinInputField
                bg="brand.lightGray2"
                borderColor={changePhoneError ? 'system.error' : 'brand.mediumGray'}
                _focusVisible={{ borderWidth: '1px', borderColor: 'brand.purple' }}
              />
              <PinInputField
                bg="brand.lightGray2"
                borderColor={changePhoneError ? 'system.error' : 'brand.mediumGray'}
                _focusVisible={{ borderWidth: '1px', borderColor: 'brand.purple' }}
              />
              <PinInputField
                bg="brand.lightGray2"
                borderColor={changePhoneError ? 'system.error' : 'brand.mediumGray'}
                _focusVisible={{ borderWidth: '1px', borderColor: 'brand.purple' }}
              />
            </PinInput>
          </HStack>
        )}

        <VStack mt="8px" spacing={0}>
          <Text textStyle="extraSmallMedium" color="brand.darkGray">
            {resendMessage}
          </Text>
          <Button
            size="xs"
            variant="link"
            onClick={handleResendCode}
            isDisabled={isResendDisabled}
            isLoading={isChekingPhone}
            textDecoration="underline"
            lineHeight="1.4"
          >
            Resend code
          </Button>
        </VStack>
      </Flex>
    </Flex>
  );
}

export default VerifyPhoneStep;
